import moment from 'moment';
import { errorUtilities } from '../common';

/**
 * All dates in the store are in yyyy-MM-dd.
 * However, some browsers (all of them except chrome and edge)
 * want to show dates in the format MM/dd/yyyy.
 * So, based on the browser we have detected,
 * we will return the properly formatted date.
 * @param {string} browserName The name of the browser.  The choices for what we support are 'chrome', 'firefox', 'ie', and 'edge'.
 * (This comes from detect-browser).
 * @param {string} dateString A date from the store, in string format, which will be yyyy-MM-dd.
 * @returns {string} A date string of the appropriate format yyyy-MM-dd or MM/dd/yyyy
 */
export function getDateForBrowser( browserName, dateString ) {
  var retval = dateString;  // if we do nothing else, just return what we were passed in
  if ( browserName !== 'chrome' && browserName !== 'edge') {
    retval = getUserFriendlyFormat(dateString);
  }
  return retval;
}

export function getUserFriendlyFormat(dateString) {
  var retval = dateString;
  if(dateString) {
    var yyyyMMddFormatted = false;
    var yyyyMMddPattern = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}");
    yyyyMMddFormatted = yyyyMMddPattern.test(dateString);
    if ( yyyyMMddFormatted ) {
      retval = dateString.substring(5,7) + "/" + dateString.substring(8) + "/" + dateString.substring(0,4);
    }
  }
  return retval;
}

/**
 * Convert a JSON date string (can be either '2016-11-02T00:00:00-05:00' or '2017-09-28T10:09:20.9546978-05:00' or '2017-09-21T00:00:00') to a user friendly date (example: '11/02/2016')
 * If the incoming date string does not contain a timezone offset, we simply extract the date part and return.
 * If the incoming date string contains a timezone offset, moment.js is used to perform a local conversion.
 * @param {*} dateString A JSON date string (can be either '2016-11-02T00:00:00-05:00' or '2017-09-28T10:09:20.9546978-05:00' or '2017-09-21T00:00:00')
 * @returns A user friendly MM/dd/yyyy date (example: '11/02/2016') if the conversion was successful.
 * If the supplied dateString does not match any of the supported date formats, the originally supplied dateString is returned.
 */
export function getUserFriendlyFormatFromJson(dateString) {
  var retval = dateString;
  if(dateString != null) {

    var timeZoneOffsetPresent = false;  // set this to true if we found a non zero timezone offset.  The offset can be positive or negative.
    // Each pattern has to be tested separately because the timezone information may appear in different places.

    var jsonDatePattern1Matched = false;  // Did we match pattern 1:  '2016-11-02T00:00:00-05:00'
    var jsonDatePattern2Matched = false;  // Did we match pattern 2:  '2017-09-28T10:09:20.9546978-05:00'
    var jsonDatePattern3Matched = false;  // Did we match pattern 3:  '2017-09-21T00:00:00'

    // pattern 1:  '2016-11-02T00:00:00-05:00'
    var jsonDatePattern1 = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}[T]\\d{2}[:]\\d{2}[:]\\d{2}[+-]\\d{2}[:]\\d{2}");
    jsonDatePattern1Matched = jsonDatePattern1.test(dateString);
    if ( jsonDatePattern1Matched ) {
      // get out the timezone offset, convert to numbers, and act accordingly
      var tzHrString = dateString.substring(20,22);
      var tzMinString = dateString.substring(23,25);

      var tzHrNum = parseInt(tzHrString, 10);
      var tzMinNum = parseInt(tzMinString, 10);

      if (tzHrNum > 0 || tzMinNum > 0) {
        timeZoneOffsetPresent = true;
      }
    }
    else {
      // pattern 2:  '2017-09-28T10:09:20.9546978-05:00'
      var jsonDatePattern2 = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}[T]\\d{2}[:]\\d{2}[:]\\d{2}[\\.]\\d{7}[+-]\\d{2}[:]\\d{2}");
      jsonDatePattern2Matched = jsonDatePattern2.test(dateString);
      if ( jsonDatePattern2Matched ) {
        // get out the timezone offset, convert to numbers, and act accordingly
        var tzHrString2 = dateString.substring(28,30);
        var tzMinString2 = dateString.substring(31,33);

        var tzHrNum2 = parseInt(tzHrString2, 10);
        var tzMinNum2 = parseInt(tzMinString2, 10);

        if (tzHrNum2 > 0 || tzMinNum2 > 0) {
          timeZoneOffsetPresent = true;
        }
      }
      else {
        // pattern 3:  '2017-09-21T00:00:00'
        var jsonDatePattern3 = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}[T]\\d{2}[:]\\d{2}[:]\\d{2}");
        jsonDatePattern3Matched = jsonDatePattern3.test(dateString);
        if ( jsonDatePattern3Matched ) {
          timeZoneOffsetPresent = false;
        }
      }
    }

    // We can test for a match in the same statement since the date parts we care about are in the same place in any case.
    // If the place where the actual date parts are ever changes, just test for each pattern matched variable independently.
    if ( jsonDatePattern1Matched || jsonDatePattern2Matched || jsonDatePattern3Matched ) {

      if (timeZoneOffsetPresent) {
        // with a nonzero utc offset, we perform a local date conversion using moment.js.
        var dateMoment = moment(dateString);

        if ( dateMoment != null) {
          retval = dateMoment.format('MM/DD/YYYY');

          if ( retval != null ) {
            return retval;
          }
        }
      }
      else {
        // with no UTC offset, we strip out just the date part and return it.
        retval = dateString.substring(5,7) + "/" + dateString.substring(8,10) + "/" + dateString.substring(0,4);
        return retval;
      }
    }
  }
  return dateString;
}

// verifies that a date string of format yyyy-mm-dd or mm/dd/yyyy is valid
export function isValidDate(dateString) {
  var mmDDyyyFormat = new RegExp("^(0?[1-9]|1[012])[/](0?[1-9]|[12][0-9]|3[01])[/]\\d{4}");
  var day, month, year;

  if(mmDDyyyFormat.test(dateString)) {
    var mmDDyyyDate = dateString.split('/');
    month = parseInt(mmDDyyyDate[0], 10);
    day = parseInt(mmDDyyyDate[1], 10);
    year = parseInt(mmDDyyyDate[2], 10);
  }
  else {
    return false;
  }

  // Create list of days of a month [assume there is no leap year by default]
  var ListofDays = [31,28,31,30,31,30,31,31,30,31,30,31];

  if( month === 2) {
    var leapYear = false;
    if ( (!(year % 4) && year % 100) || !(year % 400)) {
      leapYear = true;
    }

    if ((leapYear === false) && (day >= 29)) {
      return false;
    }
    else if ((leapYear === true) && (day > 29)) {
      return false;
    }
  }
  else if(month >= 1 && month <= 12) {
    if (day > ListofDays[month - 1]) {
      return false;
    }
  }
  else {
    return false;
  }

  return true;
}

export function isDateInRange(selectedDate, minDate, maxDate) {
  if(!selectedDate)
    return false;
  var minDateValue = minDate && Date.parse(minDate);
  var maxDateValue = maxDate && Date.parse(maxDate);
  var selectedDateValue = Date.parse(selectedDate);
  if(isNaN(selectedDateValue) || (maxDateValue && selectedDateValue > maxDateValue) || (minDateValue && selectedDateValue < minDateValue))
    return false;

  return true;
}

export function isAfterDate(value, limit) {
  if(!value)
    return false;
  var limitValue = limit && Date.parse(limit);
  var dateValue = Date.parse(value);
  if(isNaN(dateValue) || (limitValue && dateValue <= limitValue))
    return false;

  return true;
}

export function invalidDateError(id, label, section, errorNum) {
  return errorUtilities.validationError(id, label, section, errorNum, "Enter a valid date.", errorUtilities.errorTypes.inline);
}
